// Reine Berechnungen für Ranking und Statistik – keine Firestore-Zugriffe,
// kein React. Eingabe ist immer die Ranking-Liste aus den punkte-Docs:
//   { userId, username, gesamt, gesamtSpiele, gesamtBonus, wochen, bonusfragen }
// plus optional Spiele und eigene Tipps für die team-bezogenen Auswertungen.

import { NFL_TEAMS, DIVISIONS } from '../data/nflTeams';
import { istPlayoffSpiel } from './saisonPhasen';

// ---------- Wochen ----------

export const alleWochen = (ranking) => {
  const set = new Set();
  ranking.forEach(u => Object.keys(u.wochen || {}).forEach(w => set.add(parseInt(w, 10))));
  return Array.from(set).filter(w => !Number.isNaN(w)).sort((a, b) => a - b);
};

export const hatBonusfragen = (ranking) =>
  ranking.some(u => u.bonusfragen && (u.bonusfragen.gesamt || 0) > 0);

// Virtueller Datenpunkt für Bonusfragen hinter der letzten Woche
export const bonusWochenKey = (wochen) => (wochen.length > 0 ? wochen[wochen.length - 1] + 1 : 1);

const wochenPunkte = (user, week) => user.wochen?.[week]?.gesamt || 0;

// ---------- Vergleichsauswahl ----------
// auswahl: { ich, bester, durchschnitt, letzter, spieler: [userId, …] }
// Ergebnis: Serien für die Charts – Spieler (mit userId) oder Durchschnitt.

export const STANDARD_AUSWAHL = { ich: true, bester: true, durchschnitt: true, letzter: false, spieler: [] };

export function besterSpieler(ranking, uid) {
  return ranking[0]?.userId === uid ? ranking[1] : ranking[0];
}

export function letzterSpieler(ranking, uid) {
  const n = ranking.length;
  return ranking[n - 1]?.userId === uid ? ranking[n - 2] : ranking[n - 1];
}

export function serienAuswahl(ranking, auswahl, uid) {
  const serien = [];
  const gesehen = new Set();
  const add = (u) => {
    if (!u || gesehen.has(u.userId)) return;
    gesehen.add(u.userId);
    serien.push({ key: u.userId === uid ? 'Ich' : u.username, userId: u.userId, typ: 'spieler' });
  };

  if (auswahl.ich) add(ranking.find(u => u.userId === uid));
  if (auswahl.bester) add(besterSpieler(ranking, uid));
  if (auswahl.letzter) add(letzterSpieler(ranking, uid));
  ranking.forEach(u => { if (auswahl.spieler.includes(u.userId)) add(u); });
  if (auswahl.durchschnitt && ranking.length > 0) {
    serien.push({ key: 'Ø Durchschnitt', userId: null, typ: 'durchschnitt' });
  }
  return serien;
}

export const spielerSerien = (serien) => serien.filter(s => s.typ === 'spieler');

// ---------- Chart-Daten ----------

const runde1 = (x) => Math.round(x * 10) / 10;

function wochenMitBonus(ranking) {
  const wochen = alleWochen(ranking);
  const bonusKey = hatBonusfragen(ranking) ? bonusWochenKey(wochen) : null;
  return { punkte: bonusKey ? [...wochen, bonusKey] : wochen, bonusKey };
}

const wertFuerWoche = (u, week, bonusKey) =>
  (week === bonusKey ? (u.bonusfragen?.gesamt || 0) : wochenPunkte(u, week));

// Kumulierter Verlauf je Serie (Bonusfragen als letzter Punkt)
export function verlaufDaten(ranking, serien, label) {
  if (ranking.length === 0) return [];
  const { punkte, bonusKey } = wochenMitBonus(ranking);
  const laufend = {};
  ranking.forEach(u => { laufend[u.userId] = 0; });
  const nachId = Object.fromEntries(ranking.map(u => [u.userId, u]));

  return punkte.map(week => {
    ranking.forEach(u => { laufend[u.userId] += wertFuerWoche(u, week, bonusKey); });
    const zeile = { week: week === bonusKey ? 'Bonus' : label(week), weekNumber: week };
    serien.forEach(s => {
      if (s.typ === 'durchschnitt') {
        const summe = ranking.reduce((acc, u) => acc + laufend[u.userId], 0);
        zeile[s.key] = runde1(summe / ranking.length);
      } else if (nachId[s.userId]) {
        zeile[s.key] = runde1(laufend[s.userId]);
      }
    });
    return zeile;
  });
}

// Punkte je Woche je Serie; letzteN begrenzt auf die letzten N Wochen (ohne Bonus)
export function wochenDaten(ranking, serien, label, letzteN = null) {
  if (ranking.length === 0) return [];
  let { punkte, bonusKey } = wochenMitBonus(ranking);
  if (letzteN) {
    punkte = alleWochen(ranking).slice(-letzteN);
    bonusKey = null;
  }
  const nachId = Object.fromEntries(ranking.map(u => [u.userId, u]));

  return punkte.map(week => {
    const zeile = { week: week === bonusKey ? 'Bonus' : label(week), weekNumber: week };
    serien.forEach(s => {
      if (s.typ === 'durchschnitt') {
        const summe = ranking.reduce((acc, u) => acc + wertFuerWoche(u, week, bonusKey), 0);
        zeile[s.key] = runde1(summe / ranking.length);
      } else if (nachId[s.userId]) {
        zeile[s.key] = wertFuerWoche(nachId[s.userId], week, bonusKey);
      }
    });
    return zeile;
  });
}

// Platz je Woche je Spieler-Serie (Durchschnitt hat hier keinen Sinn)
export function platzierungDaten(ranking, serien, label) {
  if (ranking.length === 0) return [];
  const wochen = alleWochen(ranking);
  const spieler = spielerSerien(serien);

  return wochen.map(week => {
    const sortiert = [...ranking]
      .map(u => ({ userId: u.userId, punkte: wochenPunkte(u, week) }))
      .sort((a, b) => b.punkte - a.punkte);
    const zeile = { week: label(week), weekNumber: week };
    spieler.forEach(s => {
      zeile[s.key] = sortiert.findIndex(x => x.userId === s.userId) + 1;
    });
    return zeile;
  });
}

export const KATEGORIE_FARBEN = { exakt: '#28a745', tendenz: '#ffc107', sieger: '#17a2b8', falsch: '#dc3545' };

const summeKategorien = (wochen) => {
  const s = { exakt: 0, tendenz: 0, sieger: 0, falsch: 0 };
  Object.values(wochen || {}).forEach(w => {
    s.exakt += w.exakt || 0;
    s.tendenz += w.tendenz || 0;
    s.sieger += w.sieger || 0;
    s.falsch += w.falsch || 0;
  });
  return s;
};

// Genauigkeit je Woche (gestapelt je Spieler-Serie) und Summen je Spieler
export function genauigkeitDaten(ranking, serien, label) {
  const wochen = alleWochen(ranking);
  const spieler = spielerSerien(serien);
  const nachId = Object.fromEntries(ranking.map(u => [u.userId, u]));

  const weekly = wochen.map(week => {
    const zeile = { week: label(week), weekNumber: week };
    spieler.forEach(s => {
      const d = nachId[s.userId]?.wochen?.[week] || {};
      zeile[`${s.key}_exakt`] = d.exakt || 0;
      zeile[`${s.key}_tendenz`] = d.tendenz || 0;
      zeile[`${s.key}_sieger`] = d.sieger || 0;
      zeile[`${s.key}_falsch`] = d.falsch || 0;
    });
    return zeile;
  });

  const totals = spieler.map(s => {
    const k = summeKategorien(nachId[s.userId]?.wochen);
    return { name: s.key, userId: s.userId, ...k, gesamt: k.exakt + k.tendenz + k.sieger + k.falsch };
  });

  return { weekly, totals };
}

export function besteUndSchwaechsteWochen(ranking, serien) {
  const spieler = spielerSerien(serien);
  const eintraege = [];
  spieler.forEach(s => {
    const u = ranking.find(x => x.userId === s.userId);
    if (!u) return;
    Object.entries(u.wochen || {}).forEach(([week, data]) => {
      eintraege.push({ week: parseInt(week, 10), punkte: data.gesamt || 0, name: s.key, userId: u.userId });
    });
  });
  eintraege.sort((a, b) => b.punkte - a.punkte);
  return { best: eintraege.slice(0, 3), worst: eintraege.slice(-3).reverse() };
}

export function punkteVerteilung(ranking, currentUid) {
  const ich = ranking.find(u => u.userId === currentUid);
  if (!ich) return [];
  return [
    { name: 'Spieltipps', value: ich.gesamtSpiele || 0, color: '#5a5a5a' },
    { name: 'Bonusfragen', value: ich.gesamtBonus || 0, color: '#8a8a8a' }
  ];
}

// ---------- Tipp-Bewertung (nur Anzeige – die Punkte rechnet die Function) ----------

const tippVorhanden = (tipp) =>
  tipp && tipp.heimScore !== undefined && tipp.heimScore !== null && tipp.heimScore !== '' &&
  tipp.gastScore !== undefined && tipp.gastScore !== null && tipp.gastScore !== '';

export function tippKategorie(tipp, spiel) {
  if (!tippVorhanden(tipp)) return null;
  const heimReal = parseInt(spiel.heimTeam?.score, 10);
  const gastReal = parseInt(spiel.gastTeam?.score, 10);
  const heimTipp = parseInt(tipp.heimScore, 10);
  const gastTipp = parseInt(tipp.gastScore, 10);
  if ([heimReal, gastReal, heimTipp, gastTipp].some(Number.isNaN)) return null;

  const sieger = (h, g) => (h > g ? 'heim' : h < g ? 'gast' : 'unentschieden');
  if (heimReal === heimTipp && gastReal === gastTipp) return 'exakt';
  if (sieger(heimReal, gastReal) === sieger(heimTipp, gastTipp) && heimReal - gastReal === heimTipp - gastTipp) return 'tendenz';
  if (sieger(heimReal, gastReal) === sieger(heimTipp, gastTipp) && sieger(heimReal, gastReal) !== 'unentschieden') return 'sieger';
  return 'falsch';
}

export const tippRichtig = (tipp, spiel) => {
  const k = tippKategorie(tipp, spiel);
  return k !== null && k !== 'falsch';
};

const prozent = (richtig, gesamt) => (gesamt > 0 ? Number(((richtig / gesamt) * 100).toFixed(1)) : 0);

// ---------- Persönliche Insights (braucht Spiele + eigene Tipps) ----------

export function persoenlicheInsights(alleSpiele, userTipps) {
  if (!alleSpiele || alleSpiele.length === 0) return null;
  const teamStats = {};
  const heim = { richtig: 0, falsch: 0, gesamt: 0 };
  const away = { richtig: 0, falsch: 0, gesamt: 0 };
  const regular = { richtig: 0, gesamt: 0 };
  const playoff = { richtig: 0, gesamt: 0 };

  alleSpiele.filter(s => s.completed).forEach(spiel => {
    const tipp = userTipps[spiel.id];
    const kategorie = tippKategorie(tipp, spiel);
    if (kategorie === null) return;
    const richtig = kategorie !== 'falsch';

    [spiel.heimTeam?.shortName, spiel.gastTeam?.shortName].forEach(team => {
      if (!team) return;
      if (!teamStats[team]) teamStats[team] = { richtig: 0, gesamt: 0 };
      teamStats[team].gesamt++;
      if (richtig) teamStats[team].richtig++;
    });

    const h = parseInt(spiel.heimTeam?.score, 10);
    const g = parseInt(spiel.gastTeam?.score, 10);
    if (h > g) {
      heim.gesamt++;
      if (richtig) heim.richtig++; else heim.falsch++;
    } else if (g > h) {
      away.gesamt++;
      if (richtig) away.richtig++; else away.falsch++;
    }

    const phase = istPlayoffSpiel(spiel) ? playoff : regular;
    phase.gesamt++;
    if (richtig) phase.richtig++;
  });

  const teamPerformance = Object.entries(teamStats)
    .map(([team, s]) => ({ team, teamName: NFL_TEAMS[team]?.name || team, ...s, prozent: prozent(s.richtig, s.gesamt) }))
    .filter(t => t.gesamt >= 2)
    .sort((a, b) => b.prozent - a.prozent);

  return {
    teamPerformance: { beste: teamPerformance.slice(0, 5), schlechteste: teamPerformance.slice(-5).reverse() },
    homeAway: {
      verteilung: { heimsiege: heim.richtig, awaysiege: away.richtig, gesamt: heim.richtig + away.richtig },
      heimsiege: { ...heim, prozent: prozent(heim.richtig, heim.gesamt) },
      awaysiege: { ...away, prozent: prozent(away.richtig, away.gesamt) }
    },
    regularPlayoff: {
      verteilung: { regular: regular.gesamt, playoff: playoff.gesamt, gesamt: regular.gesamt + playoff.gesamt },
      regular: { ...regular, falsch: regular.gesamt - regular.richtig, prozent: prozent(regular.richtig, regular.gesamt) },
      playoff: { ...playoff, falsch: playoff.gesamt - playoff.richtig, prozent: prozent(playoff.richtig, playoff.gesamt) }
    }
  };
}

// ---------- Lieblingsteam ----------

const istTeamSpiel = (spiel, team) =>
  spiel.heimTeam?.shortName === team || spiel.gastTeam?.shortName === team;

export function favoritenTeamStats(alleSpiele, userTipps, team) {
  if (!team || !alleSpiele) return null;
  const spiele = alleSpiele.filter(s => istTeamSpiel(s, team) && s.completed)
    .sort((a, b) => new Date(a.datum) - new Date(b.datum));

  const z = { exakt: 0, tendenz: 0, sieger: 0, falsch: 0 };
  let getippt = 0;
  let wins = 0;
  let losses = 0;
  let laengsteStreak = 0;
  let streak = 0;

  spiele.forEach(spiel => {
    const h = parseInt(spiel.heimTeam?.score || 0, 10);
    const g = parseInt(spiel.gastTeam?.score || 0, 10);
    const teamHeim = spiel.heimTeam?.shortName === team;
    const teamPunkte = teamHeim ? h : g;
    const gegnerPunkte = teamHeim ? g : h;
    if (teamPunkte > gegnerPunkte) wins++;
    else if (teamPunkte < gegnerPunkte) losses++;

    const kategorie = tippKategorie(userTipps[spiel.id], spiel);
    if (kategorie === null) return;
    getippt++;
    z[kategorie]++;
    if (kategorie !== 'falsch') {
      streak++;
      laengsteStreak = Math.max(laengsteStreak, streak);
    } else {
      streak = 0;
    }
  });

  const richtig = z.exakt + z.tendenz + z.sieger;
  return {
    teamName: NFL_TEAMS[team]?.name || team,
    gespielt: getippt,
    gesamt: spiele.length,
    richtig,
    ...z,
    genauigkeit: prozent(richtig, getippt),
    teamRecord: `${wins}-${losses}`,
    laengsteStreak,
    aktuelleStreak: streak
  };
}

export function divisionRivalitaeten(alleSpiele, userTipps, team) {
  if (!team || !alleSpiele) return null;
  const division = Object.entries(DIVISIONS).find(([, teams]) => teams.includes(team));
  if (!division) return null;
  const [divisionName, teams] = division;

  const rivalitaeten = teams.filter(t => t !== team).map(gegner => {
    const spiele = alleSpiele.filter(s => s.completed && istTeamSpiel(s, team) && istTeamSpiel(s, gegner));
    let richtig = 0;
    let gesamt = 0;
    spiele.forEach(spiel => {
      const k = tippKategorie(userTipps[spiel.id], spiel);
      if (k === null) return;
      gesamt++;
      if (k !== 'falsch') richtig++;
    });
    return { gegner, gegnerName: NFL_TEAMS[gegner]?.name || gegner, richtig, gesamt, prozent: gesamt > 0 ? Math.round((richtig / gesamt) * 100) : 0 };
  }).sort((a, b) => b.prozent - a.prozent);

  return { divisionName, rivalitaeten };
}

// Punkte aller Spieler nur aus Spielen des Lieblingsteams – anteilig aus
// den Wochenpunkten geschätzt (die punkte-Docs anderer Spieler enthalten
// keine Spieldetails für Fremde).
export function favoritenRanking(ranking, alleSpiele, team) {
  if (!team || !alleSpiele) return [];
  const spieleNachWoche = {};
  alleSpiele.filter(s => istTeamSpiel(s, team)).forEach(s => {
    spieleNachWoche[s.week] = (spieleNachWoche[s.week] || 0) + 1;
  });

  return ranking.map(u => {
    let punkte = 0;
    Object.entries(spieleNachWoche).forEach(([week, anzahl]) => {
      const d = u.wochen?.[week];
      if (!d || !d.gesamt) return;
      const spieleWoche = (d.exakt || 0) + (d.tendenz || 0) + (d.sieger || 0) + (d.falsch || 0);
      if (spieleWoche > 0) punkte += d.gesamt * (anzahl / spieleWoche);
    });
    return { userId: u.userId, username: u.username, punkte };
  }).sort((a, b) => b.punkte - a.punkte);
}

// ---------- Tabelle ----------

export function punkteFuerAnsicht(user, ansicht) {
  switch (ansicht) {
    case 'gesamt': return user.gesamt || 0;
    case 'spiele': return user.gesamtSpiele || 0;
    case 'bonus': return user.gesamtBonus || 0;
    case 'bonusPre': return user.bonusfragen?.preSeason || 0;
    case 'bonusPlayoff': return user.bonusfragen?.playoff || 0;
    default: return wochenPunkte(user, ansicht);
  }
}

// Sortiert und vergibt Ränge; Punktgleiche teilen sich den Rang.
export function tabelle(ranking, ansicht) {
  const zeilen = ranking
    .map(u => ({ ...u, punkte: punkteFuerAnsicht(u, ansicht) }))
    .sort((a, b) => b.punkte - a.punkte);
  let rang = 1;
  return zeilen.map((z, i) => {
    if (i > 0 && Math.abs(zeilen[i - 1].punkte - z.punkte) > 0.001) rang = i + 1;
    return { ...z, rang, rueckstand: zeilen[0].punkte - z.punkte };
  });
}
